import { Paragraph, TableRow, AlignmentType, PageOrientation } from "docx";
import * as m from "@app/paraglide/messages.js";
import tcLib from '@cclib/lib/timecode.js';
import {
    createHeaderCell,
    createDataCell,
    createRow,
    formatTimecode,
    formatDuration,
    buildContentWithTags,
    SHADING_LIGHT,
} from "../helpers.js";
import convertToFormattedTextRuns from "../convertToFormattedTextRuns.js";

/**
 * Starter CCSL template.
 * 9 columns with light blue shading, landscape orientation.
 * Completely ignores the spotting event group — processes only dialogue events.
 * Reads event.speakers and event.original for role/original dialogue columns.
 * Uses only the main section (no head/metadata/footer).
 */
export default {
    id: "starter",

    getDocumentConfig() {
        return {
            orientation: PageOrientation.LANDSCAPE,
            useSections: false,
            hasPageHeader: false
        };
    },

    /**
     * Returns the header row for the starter template.
     * @returns {TableRow[]}
     */
    getHeaders() {
        return [new TableRow({
            tableHeader: true,
            children: [
                createHeaderCell(m.ccsl_export_header_timecode(), AlignmentType.CENTER, SHADING_LIGHT),
                createHeaderCell(m.ccsl_export_header_action(), AlignmentType.CENTER, SHADING_LIGHT),
                createHeaderCell(m.ccsl_export_header_number(), AlignmentType.CENTER, SHADING_LIGHT),
                createHeaderCell(m.ccsl_export_header_in(), AlignmentType.CENTER, SHADING_LIGHT),
                createHeaderCell(m.ccsl_export_header_out(), AlignmentType.CENTER, SHADING_LIGHT),
                createHeaderCell(m.ccsl_export_header_duration(), AlignmentType.CENTER, SHADING_LIGHT),
                createHeaderCell(m.ccsl_export_header_role(), AlignmentType.CENTER, SHADING_LIGHT),
                createHeaderCell(m.ccsl_export_header_original_dialogue(), AlignmentType.CENTER, SHADING_LIGHT),
                createHeaderCell(m.ccsl_export_header_english_subtitles(), AlignmentType.CENTER, SHADING_LIGHT),
            ]
        })];
    },

    /**
     * Starter template ignores spotting events — delegates to the same row builder.
     * @param {object} event - The dialogue event
     * @param {number} index - Event index
     * @param {Array} _spottingEvents - Unused
     * @param {object} _spottingEventGroup - Unused
     * @param {object} project - Project state
     * @returns {TableRow[]}
     */
    buildDialogueRowWithSpotting(event, index, _spottingEvents, _spottingEventGroup, project) {
        return this._buildRow(event, index, project);
    },

    /**
     * Builds a table row for a dialogue event.
     * @param {object} event - The dialogue event
     * @param {number} index - Event index
     * @param {object} project - Project state
     * @returns {TableRow[]}
     */
    buildDialogueRowWithoutSpotting(event, index, project) {
        return this._buildRow(event, index, project);
    },

    /**
     * Internal: builds the 9-cell row for a dialogue event.
     * @private
     */
    _buildRow(event, index, project) {
        const speakerNames = event.speakers ? event.speakers.map(s => s.name).join(", ") : "";

        return [createRow([
            // TIMECODE - show project incode in first row only
            createDataCell(index === 0
                ? tcLib.secToTc(parseFloat(project.incode), project.frameRate, project.dropFrame)
                : ""),
            // ACTION - event notes with tags
            createDataCell(buildContentWithTags(event.tags, event.notes)),
            // NUMBER - 1/ + index
            createDataCell(`1/${index + 1}`),
            // IN - timecode start
            createDataCell(formatTimecode(event.start, project)),
            // OUT - timecode end
            createDataCell(formatTimecode(event.end, project)),
            // DURATION
            createDataCell(formatDuration(event.start, event.end, project)),
            // ROLE - speaker name
            createDataCell(speakerNames),
            // ORIGINAL DIALOGUE
            createDataCell(
                new Paragraph({ children: convertToFormattedTextRuns(event.original || "") })
            ),
            // ENGLISH SUBTITLES with tags
            createDataCell(
                new Paragraph({ children: convertToFormattedTextRuns(event.text) })
            ),
        ])];
    }
};
